import React from "react";

import { Grid, Box, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";

import { DifferentLocation } from "../../../../../../../models/location";
import { Supplier } from "../../../../../../../models/supplier";
import { useAppState } from "../../../../../../../context/StateContext";
import { Status } from "../../../../../../../util/enums";
import { Select } from "../../../../../../../components/Form";
import Input from "../../../../../../../components/Form/Input";
import PhoneInput from "../../../../../../../components/Form/PhoneInput";
import ActionButtons from "../../../../../../../components/Generic/ActionButtons";
import AddressInfoForm from "../../../../../../../components/Generic/AddressComponent/AddressInfoForm";
import SkeletonDetails from "./skeleton";
import useStyles from "./styles";

interface SupplierDetailsProps {
  supplier?: Supplier;
  updateSupplier: (attribute: string, value: any) => void;
  saveSupplier: () => void;
  viewOnly: boolean;
  goBack: () => void;
  fieldHasError: (field: string) => boolean;
  getErrorByField: (field: string) => string | undefined;
  isUpdatingAction: boolean;
  showActionButtons?: boolean;
  responseError?: Error | undefined;
}

const SupplierDetails = ({
  supplier,
  updateSupplier,
  saveSupplier,
  viewOnly = false,
  goBack,
  fieldHasError,
  getErrorByField,
  isUpdatingAction,
  showActionButtons = true,
  responseError,
}: SupplierDetailsProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { isFMorSPVersion } = useAppState();

  return (
    <Box
      minHeight="inherit"
      height="100%"
      className={classes.container}
      data-cy="supplier-maintenance-details"
    >
      <Grid
        className={classes.gridHeight}
        container
        direction="column"
        justifyContent="flex-start"
        spacing={2}
      >
        <Grid item xs={12}>
          <h2 className={classes.subtitle}>{translate("supplierDetails")}</h2>
        </Grid>
        <Grid item container direction="row" justifyContent="flex-start" spacing={2}>
          <Grid item xs={6} md={3}>
            <Input
              name="code"
              data-cy="code"
              label={translate("supplierCode")}
              placeholder={translate("supplierCode")}
              value={supplier?.code}
              disabled={viewOnly}
              onChange={(event) => updateSupplier("code", event.target.value)}
              error={fieldHasError("code")}
              errorMessage={getErrorByField("code")}
              errorResponse={responseError}
              required
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Input
              name="name"
              data-cy="name"
              label={translate("supplier")}
              placeholder={translate("supplierName")}
              value={supplier?.name}
              disabled={viewOnly}
              onChange={(event) => updateSupplier("name", event.target.value)}
              error={fieldHasError("name")}
              errorMessage={getErrorByField("name")}
              required
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Input
              name="contactName"
              data-cy="contact-name"
              label={translate("mainContact")}
              placeholder={translate("mainContact")}
              value={supplier?.contactName}
              disabled={viewOnly}
              onChange={(event) => updateSupplier("contactName", event.target.value)}
              error={fieldHasError("contactName")}
              errorMessage={getErrorByField("contactName")}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Select
              dataCy="status"
              name="supplier_status"
              label={translate("status")}
              onChange={(event: SelectChangeEvent<any>) =>
                updateSupplier("status", event.target.value)
              }
              value={supplier?.status}
              data-cy="status"
              disabled={viewOnly}
            >
              <MenuItem value={Status.ACTIVE}>{translate("active")}</MenuItem>
              <MenuItem value={Status.INACTIVE}>{translate("inactive")}</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={6} md={3}>
            <Input
              name="trading_name"
              data-cy="trading-name"
              label={translate("tradingName")}
              placeholder={translate("tradingName")}
              value={supplier?.tradingName}
              disabled={viewOnly}
              onChange={(event) => updateSupplier("tradingName", event.target.value)}
              error={fieldHasError("tradingName")}
              errorMessage={getErrorByField("tradingName")}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Input
              name="supplier_email"
              data-cy="email"
              label={translate("email")}
              placeholder={translate("sampleEmailCom")}
              value={supplier?.email}
              disabled={viewOnly}
              onChange={(event) => updateSupplier("email", event.target.value)}
              error={fieldHasError("email")}
              errorMessage={getErrorByField("email")}
              required
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <PhoneInput
              data-cy="phone"
              name="business_number"
              defaultCountry="AU"
              international
              label={translate("businessNumber")}
              placeholder={"0298740011"}
              value={supplier?.phone}
              disabled={viewOnly}
              onChange={(value) => updateSupplier("phone", value || "")}
              error={fieldHasError("phone")}
              errorMessage={getErrorByField("phone")}
              required
            />
          </Grid>
          {isFMorSPVersion && (
            <Grid item xs={6} md={3}>
              <Input
                name="supplier_beCode"
                data-cy="beCode"
                label={translate("beCode")}
                placeholder={translate("beCodeSupplierExample")}
                value={supplier?.beCode}
                disabled={viewOnly}
                onChange={(event) => updateSupplier("beCode", event.target.value)}
                error={fieldHasError("beCode")}
                errorMessage={getErrorByField("beCode")}
                required
              />
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <h2 className={classes.subtitle}>{translate("address")}</h2>
        </Grid>
        <Grid item container direction="row" justifyContent="flex-start" spacing={2}>
          <Grid item xs={12} md={6}>
            <AddressInfoForm
              view={viewOnly}
              containerClass="p-0"
              addressesArray={supplier?.location ? [supplier?.location] : [{} as DifferentLocation]}
              hasToShowOnlyOne={true}
              isExpandedAddress={true}
              condition=""
              update={(value) => {
                updateSupplier("location", value[0]);
              }}
              hasError={fieldHasError("location")}
              errorMessage={getErrorByField("location")}
              dataCy="address"
            />
          </Grid>
        </Grid>
        {showActionButtons && (
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection={"row"}
              justifyContent={"flex-end"}
              className={classes.buttons}
            >
              <ActionButtons
                onSave={saveSupplier}
                onCancel={goBack}
                saveDisabled={viewOnly}
                cancelDisabled={isUpdatingAction}
                loading={isUpdatingAction}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export { SkeletonDetails };
export default SupplierDetails;
